// src/utils/cryptoCsv.ts
// เตรียมไฟล์ CSV ของพอร์ตคริปโตก่อนส่งหลังบ้าน
// - ผู้ใช้กรอกได้แค่ BUY / SELL
// - หลังบ้านไม่มี AUTO_OPENING_CASH จึงคำนวณเงินสดที่ต้องใช้เอง (ตรงตัวแบบ decimal string)
//   แล้วแทรกแถว OPENING_CASH เป็นรายการแรก ก่อนรายการทั้งหมด 1 วัน
import { addDecimal, compareDecimal, multiplyDecimal, subtractDecimal, validateCryptoDecimal } from './cryptoDecimal';

export const CRYPTO_CSV_COLUMNS = ['date', 'time', 'action', 'ticker', 'qty', 'price', 'fee', 'tax', 'currency', 'amount', 'cost', 'remark'] as const;
export const CRYPTO_CSV_ALLOWED_ACTIONS = ['BUY', 'SELL'];

export interface CryptoCsvRowError {
  row: number; // เลขบรรทัดในไฟล์ (หัวคอลัมน์ = 1)
  field?: string;
  message: string;
}

export interface CryptoCsvPrepared {
  rowCount: number;
  errors: CryptoCsvRowError[];
  openingCash: string | null; // null = ไม่ต้องใช้เงินตั้งต้น
  openingDate: string | null; // ISO (UTC)
  csvText: string; // ไฟล์ที่จะส่งจริง (รูปแบบ 12 คอลัมน์มาตรฐาน)
}

// แยก CSV ตาม RFC 4180 อย่างง่าย (รองรับ "..." ที่มี , หรือขึ้นบรรทัดใหม่ และ "" แทน ")
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field); rows.push(row); row = []; field = '';
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(f => f.trim() !== ''));
}

const csvField = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const toCsvLine = (fields: string[]) => fields.map(csvField).join(',');

// ปัดขึ้นเป็นหลักเซนต์ (USD) เพื่อไม่ให้ทศนิยมเกินขีดจำกัด และเงินพอเสมอ
export function ceilToCents(v: string): string {
  const [intPart, frac = ''] = v.split('.');
  if (frac.length <= 2) return v;
  const kept = `${intPart}.${frac.slice(0, 2)}`;
  return /^0*$/.test(frac.slice(2)) ? kept : addDecimal(kept, '0.01');
}

const pad = (n: number) => String(n).padStart(2, '0');
const formatUtcDate = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const formatUtcTime = (d: Date) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;

export function prepareCryptoCsv(text: string): CryptoCsvPrepared {
  const errors: CryptoCsvRowError[] = [];
  const table = parseCsv(text);
  const empty = { rowCount: 0, errors, openingCash: null, openingDate: null, csvText: '' };
  if (table.length === 0) {
    errors.push({ row: 1, message: 'ไฟล์ว่าง' });
    return empty;
  }

  const header = table[0].map(h => h.trim().toLowerCase());
  const col = (name: string) => header.indexOf(name);
  const missing = ['date', 'time', 'action', 'ticker', 'qty', 'price'].filter(c => col(c) === -1);
  if (missing.length) {
    errors.push({ row: 1, message: `ไม่พบคอลัมน์ที่จำเป็น: ${missing.join(', ')}` });
    return empty;
  }

  const dataRows = table.slice(1);
  if (dataRows.length === 0) errors.push({ row: 2, message: 'ไม่มีรายการในไฟล์' });
  if (dataRows.length > 10000) errors.push({ row: 1, message: 'ไฟล์มีเกิน 10,000 แถว' });

  type Parsed = { row: number; at: number; fields: Record<string, string> };
  const parsed: Parsed[] = [];

  dataRows.forEach((r, idx) => {
    const row = idx + 2;
    const get = (name: string) => (col(name) === -1 ? '' : (r[col(name)] ?? '').trim());
    const fields: Record<string, string> = {};
    CRYPTO_CSV_COLUMNS.forEach(c => { fields[c] = get(c); });
    fields.action = fields.action.toUpperCase();
    const before = errors.length;

    if (!CRYPTO_CSV_ALLOWED_ACTIONS.includes(fields.action)) {
      errors.push({ row, field: 'action', message: `รองรับเฉพาะ BUY / SELL (พบ "${fields.action || 'ว่าง'}") — เงินสดตั้งต้นระบบคำนวณให้เอง` });
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fields.date)) errors.push({ row, field: 'date', message: 'รูปแบบวันที่ต้องเป็น YYYY-MM-DD' });
    if (!/^\d{2}:\d{2}:\d{2}$/.test(fields.time)) errors.push({ row, field: 'time', message: 'รูปแบบเวลาต้องเป็น HH:mm:ss (UTC)' });
    if (!fields.ticker) errors.push({ row, field: 'ticker', message: 'กรุณาระบุเหรียญ' });
    if (fields.currency && fields.currency.toUpperCase() !== 'USD') errors.push({ row, field: 'currency', message: 'รองรับเฉพาะ USD' });

    const qtyErr = validateCryptoDecimal(fields.qty);
    if (qtyErr) errors.push({ row, field: 'qty', message: qtyErr });
    const priceErr = validateCryptoDecimal(fields.price);
    if (priceErr) errors.push({ row, field: 'price', message: priceErr });
    (['fee', 'tax'] as const).forEach(f => {
      if (!fields[f]) return;
      const err = validateCryptoDecimal(fields[f], { allowZero: true });
      if (err) errors.push({ row, field: f, message: err });
    });
    if (fields.amount) errors.push({ row, field: 'amount', message: 'แถว BUY / SELL ไม่ต้องใส่ amount' });

    const at = Date.parse(`${fields.date}T${fields.time}Z`);
    if (errors.length === before && Number.isNaN(at)) errors.push({ row, field: 'date', message: 'วันที่/เวลาไม่ถูกต้อง' });
    parsed.push({ row, at, fields });
  });

  if (errors.length) return { ...empty, rowCount: dataRows.length };

  // ไล่กระแสเงินสดตามลำดับเวลา (เวลาเท่ากันใช้ลำดับในไฟล์) หาจุดที่ติดลบลึกสุด = เงินตั้งต้นที่ต้องใช้
  let balance = '0';
  let minBalance = '0';
  [...parsed]
    .sort((a, b) => a.at - b.at || a.row - b.row)
    .forEach(({ fields }) => {
      const gross = multiplyDecimal(fields.qty, fields.price);
      const charges = addDecimal(fields.fee || '0', fields.tax || '0');
      balance = fields.action === 'BUY'
        ? subtractDecimal(balance, addDecimal(gross, charges))
        : addDecimal(balance, subtractDecimal(gross, charges));
      if (compareDecimal(balance, minBalance) < 0) minBalance = balance;
    });

  const needed = minBalance === '0' ? null : ceilToCents(minBalance.replace(/^-/, ''));
  const earliest = Math.min(...parsed.map(p => p.at));
  const openingAt = new Date(earliest - 24 * 60 * 60 * 1000); // ก่อนรายการแรก 1 วัน (เหมือนพอร์ตหุ้น)

  const lines = [CRYPTO_CSV_COLUMNS.join(',')];
  if (needed) {
    lines.push(toCsvLine([
      formatUtcDate(openingAt), formatUtcTime(openingAt), 'OPENING_CASH', '', '', '', '', '', 'USD', needed, '', 'Auto opening cash (CSV import)',
    ]));
  }
  parsed.forEach(({ fields }) => {
    lines.push(toCsvLine(CRYPTO_CSV_COLUMNS.map(c => (c === 'currency' ? (fields.currency || 'USD').toUpperCase() : fields[c]))));
  });

  return {
    rowCount: parsed.length,
    errors,
    openingCash: needed,
    openingDate: needed ? openingAt.toISOString() : null,
    csvText: lines.join('\n') + '\n',
  };
}
